import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Badge, Button, Spinner, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { Package, Download, Tag, CheckCircle, AlertTriangle, Eye, FileText } from 'react-feather';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const StoreFulfillment = () => {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [deliveryTags, setDeliveryTags] = useState([]);
  const [fulfillment, setFulfillment] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Filters
  const [deliveryTagFilter, setDeliveryTagFilter] = useState('All');
  const [storeFilter, setStoreFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal for order details
  const [detailModal, setDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Modal for store selection when generating packaging list
  const [packagingModal, setPackagingModal] = useState(false);
  const [selectedStoreForPackaging, setSelectedStoreForPackaging] = useState('');

  const fetchFulfillment = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stores/fulfillment`);
      if (response.data.success) {
        setStores(response.data.stores);
        setDeliveryTags(response.data.deliveryTags);
        setFulfillment(response.data.fulfillment);
        setSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Error fetching fulfillment data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFulfillment();
  }, [fetchFulfillment]);

  // Get counts by delivery tag
  const getDeliveryTagCounts = () => {
    const counts = {
      all: fulfillment.length,
      none: fulfillment.filter(o => !o.delivery_tag_id).length
    };
    deliveryTags.forEach(tag => {
      counts[tag.id] = fulfillment.filter(o => o.delivery_tag_id === tag.id).length;
    });
    return counts;
  };

  // Get counts by store fulfillment
  const getStoreCounts = () => {
    const counts = {
      all: fulfillment.length,
      canFulfill: fulfillment.filter(o => o.canFulfillBy.length > 0).length,
      partial: fulfillment.filter(o => o.canFulfillBy.length === 0 && o.partialFulfillBy.length > 0).length,
      noStock: fulfillment.filter(o => o.canFulfillBy.length === 0 && o.partialFulfillBy.length === 0).length
    };
    stores.forEach(store => {
      // Count orders where store can fulfill fully OR partially
      counts[store.id] = fulfillment.filter(o =>
        o.canFulfillBy.some(s => s.storeId === store.id) ||
        o.partialFulfillBy.some(s => s.storeId === store.id)
      ).length;
    });
    return counts;
  };

  // Filter orders
  const getFilteredOrders = () => {
    let filtered = fulfillment;

    // Apply delivery tag filter
    if (deliveryTagFilter !== 'All') {
      if (deliveryTagFilter === 'None') {
        filtered = filtered.filter(o => !o.delivery_tag_id);
      } else {
        filtered = filtered.filter(o => o.delivery_tag_id === parseInt(deliveryTagFilter));
      }
    }

    // Apply store filter
    if (storeFilter !== 'All') {
      if (storeFilter === 'canFulfill') {
        filtered = filtered.filter(o => o.canFulfillBy.length > 0);
      } else if (storeFilter === 'partial') {
        filtered = filtered.filter(o => o.canFulfillBy.length === 0 && o.partialFulfillBy.length > 0);
      } else if (storeFilter === 'noStock') {
        filtered = filtered.filter(o => o.canFulfillBy.length === 0 && o.partialFulfillBy.length === 0);
      } else {
        // Filter by specific store - show orders where store can fulfill fully OR partially
        filtered = filtered.filter(o =>
          o.canFulfillBy.some(s => s.storeId === parseInt(storeFilter)) ||
          o.partialFulfillBy.some(s => s.storeId === parseInt(storeFilter))
        );
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(o =>
        o.order_id?.toLowerCase().includes(query) ||
        o.company_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredOrderIds = getFilteredOrders().map(o => o.order_id);
    if (selectedOrders.length === filteredOrderIds.length && filteredOrderIds.every(id => selectedOrders.includes(id))) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrderIds);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailModal(true);
  };

  // Generate packaging list for a single order
  const handleGenerateSinglePackagingList = async (order, storeId) => {
    setGenerating(true);
    try {
      const store = stores.find(s => s.id === storeId);

      const response = await axios.post(
        `${API_BASE_URL}/api/stores/packaging-list/generate`,
        { storeId: storeId, orderIds: [order.order_id] },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PackingList_${order.order_id}_${store?.name || 'Store'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating packaging list:', err);
      alert('Failed to generate packaging list');
    } finally {
      setGenerating(false);
    }
  };

  // Open store selection modal for single order
  const handleDownloadForOrder = (order) => {
    setSelectedOrder(order);
    setSelectedOrders([order.order_id]);
    setPackagingModal(true);
  };

  const openPackagingModal = () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order');
      return;
    }
    setPackagingModal(true);
  };

  const handleGeneratePackagingList = async () => {
    if (!selectedStoreForPackaging) {
      alert('Please select a store');
      return;
    }

    setGenerating(true);
    try {
      let response;
      let filename;

      if (selectedStoreForPackaging === 'all') {
        // Generate combined packaging list (all items, no store split)
        response = await axios.post(
          `${API_BASE_URL}/api/stores/packaging-list/generate-all`,
          { orderIds: selectedOrders },
          { responseType: 'blob' }
        );
        filename = selectedOrders.length === 1
          ? `PackingList_${selectedOrders[0]}.pdf`
          : `PackingList_${selectedOrders.length}orders_${new Date().toISOString().split('T')[0]}.pdf`;
      } else {
        const storeId = parseInt(selectedStoreForPackaging);
        const store = stores.find(s => s.id === storeId);
        response = await axios.post(
          `${API_BASE_URL}/api/stores/packaging-list/generate`,
          { storeId: storeId, orderIds: selectedOrders },
          { responseType: 'blob' }
        );
        filename = selectedOrders.length === 1
          ? `PackingList_${store?.name || 'Store'}_${selectedOrders[0]}.pdf`
          : `PackingList_${store?.name || 'Store'}_${selectedOrders.length}orders_${new Date().toISOString().split('T')[0]}.pdf`;
      }

      // Download PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setPackagingModal(false);
      setSelectedStoreForPackaging('');
    } catch (err) {
      console.error('Error generating packaging list:', err);
      alert('Failed to generate packaging list');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateExcel = async () => {
    if (!selectedStoreForPackaging) {
      alert('Please select a store');
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/stores/packaging-list/generate-all-excel`,
        { orderIds: selectedOrders },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedOrders.length === 1
        ? `PackingList_${selectedOrders[0]}.xlsx`
        : `PackingList_${selectedOrders.length}orders.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setPackagingModal(false);
      setSelectedStoreForPackaging('');
    } catch (err) {
      console.error('Error generating Excel packaging list:', err);
      alert('Failed to generate Excel packaging list');
    } finally {
      setGenerating(false);
    }
  };

  const filteredOrders = getFilteredOrders();
  const tagCounts = getDeliveryTagCounts();
  const storeCounts = getStoreCounts();

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs mainTitle="Order Fulfillment" parent="Stores" />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardBody className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading orders...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Order Fulfillment" parent="Stores" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h5>Order Fulfillment by Store Stock</h5>
                    <span>View orders and check which stores can fulfill them based on current stock</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {selectedOrders.length > 0 && (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={openPackagingModal}
                      >
                        <Download size={14} className="me-1" />
                        Generate Packaging List ({selectedOrders.length})
                      </Button>
                    )}
                    <Input
                      type="text"
                      placeholder="Search Order ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ width: '200px' }}
                    />
                  </div>
                </div>

                {/* Store Fulfillment Tabs */}
                <div className="mt-3">
                  <style>{`
                    .status-tabs {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 8px;
                    }
                    .status-tab {
                      padding: 6px 12px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 13px;
                      font-weight: 500;
                      transition: all 0.2s ease;
                    }
                    .status-tab:hover {
                      opacity: 0.8;
                    }
                    .status-tab.active {
                      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                    }
                    .status-all { background: #e9ecef; color: #495057; }
                    .status-all.active { background: #495057; color: white; }
                    .status-can-fulfill { background: #d1e7dd; color: #0f5132; }
                    .status-can-fulfill.active { background: #198754; color: white; }
                    .status-partial { background: #fff3cd; color: #664d03; }
                    .status-partial.active { background: #ffc107; color: #000; }
                    .status-no-stock { background: #f8d7da; color: #842029; }
                    .status-no-stock.active { background: #dc3545; color: white; }
                    .status-store { background: #cfe2ff; color: #084298; }
                    .status-store.active { background: #0d6efd; color: white; }

                    .delivery-tags-section {
                      margin-top: 12px;
                      padding-top: 12px;
                      border-top: 1px solid #dee2e6;
                    }
                    .delivery-tags-label {
                      font-size: 12px;
                      font-weight: 600;
                      color: #6c757d;
                      margin-bottom: 8px;
                      display: flex;
                      align-items: center;
                      gap: 6px;
                    }
                    .delivery-tag-tab { background: #e0f2fe; color: #0369a1; }
                    .delivery-tag-tab.active { background: #0ea5e9; color: white; }
                    .delivery-tag-tab.no-tag { background: #f3f4f6; color: #6b7280; }
                    .delivery-tag-tab.no-tag.active { background: #6b7280; color: white; }
                  `}</style>

                  <div className="delivery-tags-label">
                    <Package size={14} />
                    Stock Availability:
                  </div>
                  <div className="status-tabs">
                    <span
                      className={`status-tab status-all ${storeFilter === 'All' ? 'active' : ''}`}
                      onClick={() => setStoreFilter('All')}
                    >
                      All ({storeCounts.all})
                    </span>
                    <span
                      className={`status-tab status-can-fulfill ${storeFilter === 'canFulfill' ? 'active' : ''}`}
                      onClick={() => setStoreFilter('canFulfill')}
                    >
                      <CheckCircle size={12} className="me-1" />
                      Can Fulfill ({storeCounts.canFulfill})
                    </span>
                    <span
                      className={`status-tab status-partial ${storeFilter === 'partial' ? 'active' : ''}`}
                      onClick={() => setStoreFilter('partial')}
                    >
                      <AlertTriangle size={12} className="me-1" />
                      Partial Stock ({storeCounts.partial})
                    </span>
                    <span
                      className={`status-tab status-no-stock ${storeFilter === 'noStock' ? 'active' : ''}`}
                      onClick={() => setStoreFilter('noStock')}
                    >
                      No Stock ({storeCounts.noStock})
                    </span>
                    {stores.map(store => (
                      storeCounts[store.id] > 0 && (
                        <span
                          key={store.id}
                          className={`status-tab status-store ${storeFilter === store.id.toString() ? 'active' : ''}`}
                          onClick={() => setStoreFilter(store.id.toString())}
                        >
                          {store.name} ({storeCounts[store.id]})
                        </span>
                      )
                    ))}
                  </div>
                </div>

                {/* Delivery Tag Tabs */}
                <div className="delivery-tags-section">
                  <div className="delivery-tags-label">
                    <Tag size={14} />
                    Delivery Tags:
                  </div>
                  <div className="status-tabs">
                    <span
                      className={`status-tab delivery-tag-tab ${deliveryTagFilter === 'All' ? 'active' : ''}`}
                      onClick={() => setDeliveryTagFilter('All')}
                    >
                      All ({tagCounts.all})
                    </span>
                    <span
                      className={`status-tab delivery-tag-tab no-tag ${deliveryTagFilter === 'None' ? 'active' : ''}`}
                      onClick={() => setDeliveryTagFilter('None')}
                    >
                      No Tag ({tagCounts.none})
                    </span>
                    {deliveryTags.map(tag => (
                      tagCounts[tag.id] > 0 && (
                        <span
                          key={tag.id}
                          className={`status-tab delivery-tag-tab ${deliveryTagFilter === tag.id.toString() ? 'active' : ''}`}
                          onClick={() => setDeliveryTagFilter(tag.id.toString())}
                        >
                          {tag.name} ({tagCounts[tag.id]})
                        </span>
                      )
                    ))}
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No orders found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover className="table-sm">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <Input
                              type="checkbox"
                              checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Delivery Tag</th>
                          <th>Items</th>
                          <th>Can Fulfill</th>
                          <th>Partial Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map(order => (
                          <tr key={order.order_id}>
                            <td className="text-center">
                              <Input
                                type="checkbox"
                                checked={selectedOrders.includes(order.order_id)}
                                onChange={() => handleSelectOrder(order.order_id)}
                              />
                            </td>
                            <td><strong>{order.order_id}</strong></td>
                            <td>{order.company_name || 'N/A'}</td>
                            <td>
                              {order.delivery_tag_name ? (
                                <Badge color="info">{order.delivery_tag_name}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0} items</td>
                            <td>
                              {order.canFulfillBy.length > 0 ? (
                                order.canFulfillBy.map(s => (
                                  <span
                                    key={s.storeId}
                                    className="me-2"
                                    style={{
                                      cursor: 'pointer',
                                      padding: '6px 14px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-block',
                                      backgroundColor: s.storeName === 'Venlo' ? '#0d6efd' : '#6f42c1',
                                      color: 'white'
                                    }}
                                    onClick={() => handleGenerateSinglePackagingList(order, s.storeId)}
                                    title={`Click to generate packaging list for ${s.storeName}`}
                                  >
                                    {s.storeName}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {order.partialFulfillBy.length > 0 ? (
                                order.partialFulfillBy.map(s => (
                                  <span
                                    key={s.storeId}
                                    className="me-2"
                                    style={{
                                      cursor: 'pointer',
                                      padding: '6px 14px',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'inline-block',
                                      backgroundColor: s.storeName === 'Venlo' ? '#0dcaf0' : '#e85d04',
                                      color: s.storeName === 'Venlo' ? '#000' : 'white'
                                    }}
                                    onClick={() => handleGenerateSinglePackagingList(order, s.storeId)}
                                    title={`Click to generate packaging list for ${s.storeName}`}
                                  >
                                    {s.storeName} ({s.fulfillableItems}/{s.totalItems})
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Order Details Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>
          Order Details - {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          {selectedOrder && (
            <>
              <div className="mb-3">
                <strong>Customer:</strong> {selectedOrder.company_name || 'N/A'}
              </div>
              <div className="mb-3">
                <strong>Delivery Tag:</strong>{' '}
                {selectedOrder.delivery_tag_name ? (
                  <Badge color="info">{selectedOrder.delivery_tag_name}</Badge>
                ) : (
                  <span className="text-muted">Not assigned</span>
                )}
              </div>
              <div className="mb-3">
                <strong>Can Fulfill From:</strong>{' '}
                {selectedOrder.canFulfillBy.length > 0 ? (
                  selectedOrder.canFulfillBy.map(s => (
                    <Badge key={s.storeId} color="success" className="me-1">{s.storeName}</Badge>
                  ))
                ) : (
                  <span className="text-muted">No store has full stock</span>
                )}
              </div>
              <h6 className="mt-4">Order Items:</h6>
              <Table bordered size="sm">
                <thead className="bg-light">
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Size</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td><code>{item.sku}-{item.size}</code></td>
                      <td>{item.title}</td>
                      <td>{item.size}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Packaging List Store Selection Modal */}
      <Modal isOpen={packagingModal} toggle={() => setPackagingModal(false)}>
        <ModalHeader toggle={() => setPackagingModal(false)}>
          Generate Packaging List
        </ModalHeader>
        <ModalBody>
          <p>Select the store to generate the packaging list for {selectedOrders.length} order(s):</p>
          <Input
            type="select"
            value={selectedStoreForPackaging}
            onChange={(e) => setSelectedStoreForPackaging(e.target.value)}
          >
            <option value="">-- Select Store --</option>
            <option value="all">All (Combined Packing List)</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPackagingModal(false)}>Cancel</Button>
          <Button
            color="success"
            onClick={handleGenerateExcel}
            disabled={generating || !selectedStoreForPackaging}
          >
            {generating ? <><Spinner size="sm" className="me-1" /> Generating...</> : 'Download Excel'}
          </Button>
          <Button
            color="primary"
            onClick={handleGeneratePackagingList}
            disabled={generating || !selectedStoreForPackaging}
          >
            {generating ? <><Spinner size="sm" className="me-1" /> Generating...</> : 'Generate PDF'}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default StoreFulfillment;
